import { ChevronDown, Languages, Loader2 } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { V3_MENU_CONTENT } from "@/components/ui/v3/menu";
import {
  type NoteLanguageOption,
  useNoteLanguage,
} from "@/hooks/user/useNoteLanguage";
import { cn } from "@/lib/utils";

// Shows, next to Create, the language new notes, letters and summaries are
// written in, and switches it. Same account preference as Settings →
// Transcription and the New visit sheet; existing documents are not changed.
// "EN", "FR", "中文": the note language as a chip in the v3.2 Ask bar (the
// language dictation and new documents use).
export const languageChipLabel = (
  option: Pick<NoteLanguageOption, "code" | "name" | "label">,
): string => {
  if (option.name === "Mandarin") return "中文";
  const code = (option.code ?? "").toLowerCase();
  const known: Record<string, string> = {
    eng: "EN",
    fra: "FR",
    spa: "ES",
    por: "PT",
    jpn: "JA",
    jap: "JA",
    cmn: "中文",
    zho: "中文",
  };
  return (
    known[code] ?? (code.slice(0, 2) || option.label.slice(0, 2)).toUpperCase()
  );
};

export const NoteLanguageMenu = ({
  disabled,
  variant = "button",
}: {
  disabled?: boolean;
  // "chip": the small language chip in the v3.2 Ask bar.
  variant?: "button" | "chip";
}): JSX.Element | null => {
  const { t } = useTranslation();
  const { options, current, saving, failed, chooseLanguage } =
    useNoteLanguage();
  if (!current) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "chip" ? (
          <button
            aria-label={t("noteLanguage.indicator", {
              language: current.label,
            })}
            className="inline-flex size-11 shrink-0 items-center justify-center rounded-full font-v3 disabled:opacity-40 lg:size-9"
            data-testid="note-language-chip"
            disabled={disabled || saving}
            title={t("noteLanguage.indicator", { language: current.label })}
            type="button"
          >
            <span className="inline-flex h-7 min-w-[34px] items-center justify-center rounded-full bg-v3-surface-2 px-2 text-[12px] font-semibold text-v3-text-2 shadow-v3-hairline">
              {saving ? (
                <Loader2 aria-hidden className="size-3.5 animate-spin" />
              ) : (
                languageChipLabel(current)
              )}
            </span>
          </button>
        ) : (
          <Button
            aria-label={t("noteLanguage.indicator", {
              language: current.label,
            })}
            className="h-11 gap-1.5 px-3 lg:h-9"
            data-testid="note-language-menu"
            disabled={disabled || saving}
            title={t("noteLanguage.indicator", { language: current.label })}
            type="button"
            variant="outline"
          >
            {saving ? (
              <Loader2 aria-hidden className="animate-spin" />
            ) : (
              <Languages aria-hidden />
            )}
            <span className="max-w-36 truncate">{current.label}</span>
            <ChevronDown aria-hidden className="opacity-60" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={variant === "chip" ? "end" : "start"}
        className={cn(
          "w-72 max-w-[90vw]",
          variant === "chip" && V3_MENU_CONTENT,
        )}
      >
        <DropdownMenuLabel>{t("noteLanguage.menuTitle")}</DropdownMenuLabel>
        <p className="px-2 pb-2 text-xs text-muted-foreground">
          {t("noteLanguage.menuHint")}
        </p>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={String(current.id)}
          onValueChange={(value) => {
            void chooseLanguage(Number(value));
          }}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem
              key={option.id}
              className="min-h-11 lg:min-h-9"
              value={String(option.id)}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {failed && (
          <p className="px-2 py-2 text-xs text-destructive" role="alert">
            {t("noteLanguage.saveFailed")}
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** One line for the Create menu: the language the new document is written in. */
export const NoteLanguageCaption = (): JSX.Element | null => {
  const { t } = useTranslation();
  const { current } = useNoteLanguage();
  if (!current) return null;
  return (
    <p
      className="px-3 pb-1.5 pt-1 text-xs text-muted-foreground"
      data-testid="create-menu-language"
    >
      {t("noteLanguage.writtenIn", { language: current.inline })}
    </p>
  );
};
